export type Order = 'asc' | 'desc';

type SortableByKey<T> = {
  [key in keyof T]: number | string | Date;
};

/**
 * Compares two objects in a descending order based on a specified key.
 * @param a - The first object to compare.
 * @param b - The second object to compare.
 * @param orderBy - The key of the objects on which the comparison is based.
 * @returns 0 if `a` & `b` are equal, -1 if `b` should come before `a`, and +1 if `a` should come before `b`
 */
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const aValue = a[orderBy];
  const bValue = b[orderBy];

  if (aValue instanceof Date && bValue instanceof Date) {
    // Handle Date comparison
    return bValue.getTime() - aValue.getTime();
  }

  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }

  return 0;
}

/**
 * Returns a comparator function that can be used to sort an array of objects
 * based on a specified key and order (ascending or descending).
 *
 * @template T - The type of the objects being compared. T must have properties that are
 *               either numbers, strings, or dates, as defined by `SortableByKey<T>`.
 * @template Key - The specific key of the object on which the comparison is based.
 *
 * @param order - The order of the sorting, either 'asc' for ascending or 'desc' for descending.
 * @param orderBy - The key of the objects on which the comparison is based.
 * @returns A comparator function that takes two objects of type T and returns:
 *          - A negative number if the first object should appear before the second.
 *          - A positive number if the first object should appear after the second.
 *          - Zero if the two objects are equal based on the specified key.
 *
 * The comparator function uses a descending comparison by default and inverts the result
 * if ascending order is specified.
 *
 * Sample usage: popularityRows.slice().sort(getComparator('desc', 'avgOccupancy'))
 */
export function getComparator<T extends SortableByKey<T>, Key extends keyof T>(
  order: Order,
  orderBy: Key
): (a: T, b: T) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
